import { MaybeRef, unref } from 'vue'
import { Kind } from 'graphql'
import type { DocumentNode, OperationDefinitionNode } from 'graphql'
import { convertEnumToDisplayText, textHelpers } from '@/utils/tools'
import { SortOrder, TypeRefFragment } from '@/gql/graphql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'

// Use '::' to avoid collisions with item keys that may contain a single ':'
const EXPANDED_KEY_SEPARATOR = '::'

export const encodeExpandedColumnKey = (fragmentName: string, itemKey: string): string =>
  `${fragmentName}${EXPANDED_KEY_SEPARATOR}${itemKey}`

export const decodeExpandedColumnKey = (key: string): { fragmentName: string; itemKey: string } | null => {
  const idx = key.indexOf(EXPANDED_KEY_SEPARATOR)
  if (idx === -1) return null
  return {
    fragmentName: key.slice(0, idx),
    itemKey: key.slice(idx + EXPANDED_KEY_SEPARATOR.length),
  }
}

export const containsListKind = (typeRef: { kind: string; ofType?: any } | null | undefined): boolean => {
  let t = typeRef
  while (t) {
    if (t.kind === 'LIST') return true
    t = t.ofType ?? null
  }
  return false
}

export const fieldToReadAble = (str: string | undefined): string =>
  str ? textHelpers.capitalizeFirstLetter(textHelpers.keyToHumanReadable(str)) : ''

// Helper function to get the innermost type of field (unwrapping non-null and list types)
export const getFieldTypeLeaf = (type: TypeRefFragment): TypeRefFragment => {
  // Unwrap the type until there is no more 'ofType'
  let current = type
  while (current?.ofType) {
    current = current.ofType
  }

  return current
}

export const convertArgTypeToTypeStr = (argType) => {
  switch (argType.kind) {
    case 'LIST':
      return `[${convertArgTypeToTypeStr(argType.ofType)}]`
    case 'NON_NULL':
      return `${convertArgTypeToTypeStr(argType.ofType)}!`
  }
  return argType.name
}

// Define the EnumType interface
interface EnumType {
  name: string
  description?: string | null
}

// Update the guessEnumComplimentaryText function
const guessEnumComplimentaryText = (enumValue: EnumType, sort: SortOrder): string => {
  // Extract and normalize the name and description
  const name = (enumValue.name || '').toLowerCase().replace(/_/g, ' ')
  const description = (enumValue.description || '').toLowerCase()

  // Combine name and description for keyword matching
  const combinedText = `${name} ${description}`

  // Define keyword arrays for each type
  const dateKeywords = [
    'date',
    'time',
    'start date',
    'end date',
    'created at',
    'updated at',
    'deleted at',
  ]
  const stringKeywords = ['name', 'company', 'worker', 'job', 'title']
  const numericKeywords = ['number', 'amount', 'quantity', 'rate']
  const priorityKeywords = ['priority']

  const scoreVal = {
    number: 3, // number is special and "stornger"
    date: 2,
    name: 2,
  }

  // Function to calculate score for each type
  const calculateScore = (keywords: string[]): number => {
    let score = 0
    keywords.forEach((keyword) => {
      // Use a regular expression to count keyword occurrences
      const regex = new RegExp(`\\b${keyword}\\b`, 'g')
      const matches = combinedText.match(regex)
      if (matches) {
        score += matches.length * (scoreVal[keyword] ?? 1)
      }
    })
    return score
  }

  // Create an array of type-score pairs
  const scores = [
    {
      // Priority type
      sortText: sort === SortOrder.Asc ? $t('Ascending') : $t('Descending'),
      score: calculateScore(priorityKeywords),
    },
    {
      // Date type
      sortText: sort === SortOrder.Asc ? $t('Old to new') : $t('New to old'),
      score: calculateScore(dateKeywords),
    },
    {
      // String type
      sortText: sort === SortOrder.Asc ? $t('A - Z') : $t('Z - A'),
      score: calculateScore(stringKeywords),
    },
    {
      // Numeric type
      sortText: sort === SortOrder.Asc ? $t('Less - More') : $t('More - Less'),
      score: calculateScore(numericKeywords),
    },
  ]

  // Use reduce to find the type with the highest score
  const maxScoreType = scores.reduce((max, curr) => (curr.score > max.score ? curr : max), {
    sortText: sort === SortOrder.Asc ? $t('Ascending') : $t('Descending'),
    score: 0,
  })

  // Determine the type
  return maxScoreType.sortText
}

export const generateSortOptions = (
  enumValue: EnumType,
  existingSorts: MaybeRef<Record<string, any>> = {},
) => {
  const existingSortOptions: Record<string, any> = unref(existingSorts)
  const { name, description } = enumValue
  return [SortOrder.Asc, SortOrder.Desc].flatMap((sortOrder) => {
    const key = `${name}_${sortOrder}`
    if (existingSortOptions[key]) {
      return existingSortOptions[key]
    }
    return {
      name: key,
      primaryText: convertEnumToDisplayText(name),
      secondaryText: guessEnumComplimentaryText(enumValue, sortOrder),
      orderByColumn: name,
      sortOrder,
      title: $t(description),
    }
  })
}

/** URL-state prefix for a table sharing the page with another; the operation name is unique per table. */
export function tableNamespace(query: DocumentNode): string {
  const operation = query.definitions.find(
    (definition): definition is OperationDefinitionNode =>
      definition.kind === Kind.OPERATION_DEFINITION,
  )

  const name = operation?.name?.value ?? ''

  if (name === '') return ''

  return `${name.charAt(0).toLowerCase()}${name.slice(1)}`.replace(/Query$/, '')
}

/** One mounted table: the namespaced query name plus the instance id. */
export function tableScopeId(query: DocumentNode, instanceId: string | number): string {
  return `${tableNamespace(query) || 'graphqlTable'}-${instanceId}`
}

/** Class for one table's globals wrapper, so its overlays can teleport into it by selector. */
export function tableGlobalsClass(query: DocumentNode, instanceId: string | number): string {
  return `graphql-table-globals-${tableScopeId(query, instanceId)}`
}
